import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useUserContext } from '../../context/UserContext';
import { useHouseDetails } from '../../context/HouseDetailsContext';

import {
    Container,
    Grid,
    Snackbar,
    Alert,
    AlertTitle
} from '@mui/material';

import PageTitle from '../../components/layout/PageTitle';
import HouseMonitoringWelcomeMsg from '../../components/houses/house reports/HouseMonitoringWelcomeMsg';
import HouseMonitoringAdditional from '../../components/houses/house reports/HouseMonitoringAdditional';
import HouseMonitoringConsumptionFeed from '../../components/houses/house reports/HouseMonitoringConsumptionFeed';
import HouseMonitoringPeriodicConsumptions
    from '../../components/houses/house reports/HouseMonitoringPeriodicConsumptions';
import HouseMonitoringBillingInfo from '../../components/houses/house reports/HouseMonitoringBillingInfo';

import {
    getHouseBillingInfo,
    getHouseCostHistory,
    getHouseInsights
} from '../../api_calls/api_calls_houses';

function HouseReportsNew() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useUserContext();
    const { houseName, houseAddress } = useHouseDetails();

    const [houseIdError] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [allowed, setAllowed] = useState(false);

    const [loadingInsights, setLoadingInsights] = useState(false);
    const [dayInsights, setDayInsights] = useState({});
    const [weekInsights, setWeekInsights] = useState({});
    const [monthInsights, setMonthInsights] = useState({});
    const [currentCost, setCurrentCost] = useState(0);
    const [averageCost, setAverageCost] = useState();
    const [costData, setCostData] = useState();

    useEffect(() => {
        if (!token) {
            navigate('/signin');
        } else {
            setAllowed(true);
        }
    }, [token, navigate]);

    useEffect(() => {
        if (!token) return;
        setLoadingInsights(true);
        getHouseInsights(id)
            .then(response => {
                setLoadingInsights(false);
                setDayInsights(response.day_insight);
                setWeekInsights(response.week_insight);
                setMonthInsights(response.month_insight);
            })
            .catch(() => {
                setLoadingInsights(false);
            });
    }, [id, token]);

    useEffect(() => {
        if (!token) return;

        getHouseBillingInfo(id)
            .then(response => {
                setCurrentCost(response.cost);
                setAverageCost(response.average.toFixed(2));
            })
            .catch(console.error);

        getHouseCostHistory(id)
            .then(setCostData)
            .catch(console.error);
    }, [id, token]);

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
        navigate('/houses');
    };

    return (
        <>
            <PageTitle title="Live Monitoring" />

            {allowed && houseAddress && (
                <Container sx={{ mt: 5 }} maxWidth="xl">
                    <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'stretch', my: 1 }}>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <HouseMonitoringWelcomeMsg
                                id={id}
                                houseAddress={houseAddress}
                                houseName={houseName}
                                currentCost={currentCost.toFixed(2)}
                                costData={costData}
                                monthInsights={monthInsights}
                                averageCost={averageCost}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <HouseMonitoringAdditional
                                id={id}
                                loadingInsights={loadingInsights}
                                dayInsights={dayInsights}
                                weekInsights={weekInsights}
                                currentCost={currentCost.toFixed(2)}
                                averageCost={averageCost}
                                monthInsights={monthInsights}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <HouseMonitoringBillingInfo id={id} />
                        </Grid>
                    </Grid>

                    <HouseMonitoringConsumptionFeed id={id} />
                    <HouseMonitoringPeriodicConsumptions id={id} />
                </Container>
            )}

            {allowed && houseIdError && (
                <Container sx={{ mt: 5 }}>
                    <Alert severity="error">
                        <AlertTitle>Error</AlertTitle>
                        This house does not exist. You will be navigated to the houses page.
                    </Alert>
                </Container>
            )}

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={handleSnackbarClose} severity="error" variant="filled" sx={{ width: '100%' }}>
                    This house ID is not valid.
                </Alert>
            </Snackbar>
        </>
    );
}

export default HouseReportsNew;
